import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { LogOut, Users, Key, Copy, ExternalLink, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import { he } from "date-fns/locale";

interface Lead {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  source_page: string;
  created_at: string;
  quiz_q1: string | null;
  quiz_q2: string | null;
  quiz_q3: string | null;
}

const AdminDashboard = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchLeads();
    }
  }, [isAuthenticated]);

  const checkAuth = async () => {
    setIsAuthChecking(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/admin");
        return;
      }

      const { data: isAdmin, error } = await supabase.rpc('has_role', {
        _user_id: session.user.id,
        _role: 'admin'
      });

      if (error || !isAdmin) {
        await supabase.auth.signOut();
        navigate("/admin");
        return;
      }

      setIsAuthenticated(true);
    } catch {
      navigate("/admin");
    } finally {
      setIsAuthChecking(false);
    }
  };

  const fetchLeads = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      toast({
        title: "שגיאה בטעינת הלידים",
        description: error.message,
        variant: "destructive",
      });
    } else {
      setLeads(data || []);
    }
    setIsLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin");
  };

  const generateApiKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let key = 'amon_';
    for (let i = 0; i < 32; i++) {
      key += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setApiKey(key);
    toast({
      title: "מפתח API נוצר",
      description: "העתק את המפתח ושמור אותו במקום בטוח. תצטרך לעדכן אותו ב-Supabase Secrets.",
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "הועתק ללוח",
    });
  };

  const apiEndpoint = "https://zkuiphwjzjlgzlmqmijt.supabase.co/functions/v1/get-leads";

  // Block render until auth is confirmed - prevents UI exposure to unauthorized users
  if (isAuthChecking || !isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" dir="rtl">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">מאמת הרשאות...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6" dir="rtl">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-foreground">דשבורד אדמין</h1>
          <Button variant="outline" onClick={handleLogout}>
            <LogOut className="ml-2 h-4 w-4" />
            התנתקות
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">סה"כ לידים</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{leads.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">לידים מ-Waitlist</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {leads.filter(l => l.source_page === 'waitlist').length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">לידים מקוויזים</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {leads.filter(l => l.source_page === 'quiz_men' || l.source_page === 'quiz_women').length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">לידים מדפי השירות</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {leads.filter(l => !['waitlist', 'quiz_men', 'quiz_women'].includes(l.source_page)).length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Leads Table */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>רשימת לידים</CardTitle>
              <Button variant="outline" size="sm" onClick={fetchLeads}>
                <RefreshCw className="ml-2 h-4 w-4" />
                רענון
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">טוען...</div>
            ) : leads.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">אין לידים עדיין</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-right">שם</TableHead>
                      <TableHead className="text-right">טלפון</TableHead>
                      <TableHead className="text-right">אימייל</TableHead>
                      <TableHead className="text-right">מין</TableHead>
                      <TableHead className="text-right" title="גברים: תסכול באפליקציות | נשים: דפוס בקשרים">תסכולים/דפוסים</TableHead>
                      <TableHead className="text-right" title="גברים: התנהלות באי-הסכמה | נשים: מה חסר בזוגיות">התנהלות/חיסרון</TableHead>
                      <TableHead className="text-right" title="גברים: מה מחפש | נשים: מה מונע מאפליקציות">ציפיות/מניעות</TableHead>
                      <TableHead className="text-right">תאריך</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map((lead) => (
                      <TableRow key={lead.id}>
                        <TableCell className="font-medium">{lead.name}</TableCell>
                        <TableCell dir="ltr" className="text-right">{lead.phone}</TableCell>
                        <TableCell>{lead.email || "-"}</TableCell>
                        <TableCell>
                          {lead.source_page.includes('men') && !lead.source_page.includes('women') ? 'men' : 
                           lead.source_page.includes('women') ? 'women' : '-'}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate" title={lead.quiz_q1 || ""}>
                          {lead.quiz_q1 || "-"}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate" title={lead.quiz_q2 || ""}>
                          {lead.quiz_q2 || "-"}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate" title={lead.quiz_q3 || ""}>
                          {lead.quiz_q3 || "-"}
                        </TableCell>
                        <TableCell>
                          {format(new Date(lead.created_at), "dd/MM/yyyy HH:mm", { locale: he })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* API Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              גישה ל-API
            </CardTitle>
            <CardDescription>
              השתמש ב-API כדי לשלוף את הלידים לאפליקציות חיצוניות
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">כתובת ה-API:</h4>
              <div className="flex items-center gap-2 bg-muted p-3 rounded-lg">
                <code className="text-sm flex-1 break-all" dir="ltr">
                  {apiEndpoint}?api_key=YOUR_API_KEY
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(`${apiEndpoint}?api_key=YOUR_API_KEY`)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2">יצירת מפתח API חדש:</h4>
              <div className="space-y-2">
                <Button onClick={generateApiKey}>
                  <Key className="ml-2 h-4 w-4" />
                  צור מפתח חדש
                </Button>
                {apiKey && (
                  <div className="flex items-center gap-2 bg-accent/20 p-3 rounded-lg border border-accent">
                    <code className="text-sm flex-1 break-all font-mono" dir="ltr">
                      {apiKey}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(apiKey)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-muted/50 p-4 rounded-lg space-y-3">
              <h4 className="font-semibold">הוראות הטמעה:</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                <li>
                  צור מפתח API חדש (לחץ על הכפתור למעלה)
                </li>
                <li>
                  עדכן את המפתח ב-Supabase:
                  <a
                    href="https://supabase.com/dashboard/project/zkuiphwjzjlgzlmqmijt/settings/functions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-1 mr-1"
                  >
                    הגדרות Edge Functions
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </li>
                <li>עדכן את הסוד <code className="bg-muted px-1 rounded">LEADS_API_KEY</code> עם המפתח החדש</li>
                <li>
                  קרא ל-API עם המפתח:
                  <pre className="bg-background p-2 rounded mt-1 text-xs overflow-x-auto" dir="ltr">
{`curl "${apiEndpoint}?api_key=YOUR_API_KEY"`}
                  </pre>
                </li>
              </ol>
            </div>

            <div className="bg-muted/50 p-4 rounded-lg">
              <h4 className="font-semibold mb-2">תשובת ה-API:</h4>
              <pre className="bg-background p-3 rounded text-xs overflow-x-auto" dir="ltr">
{`{
  "success": true,
  "count": ${leads.length},
  "leads": [
    {
      "id": "uuid",
      "name": "שם הליד",
      "phone": "0501234567",
      "email": "email@example.com",
      "source_page": "men",
      "created_at": "2025-01-01T12:00:00Z",
      "quiz_q1": "תשובה לשאלה 1",
      "quiz_q2": "תשובה לשאלה 2",
      "quiz_q3": "תשובה לשאלה 3"
    }
  ]
}`}
              </pre>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
